import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../utils/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { CurrencyInput } from "../components/ui/currency-input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Plus, Pencil, Trash2, Wallet, FileText, Printer, ArrowRightLeft } from "lucide-react";
import AccountMovements from "../components/AccountMovements";
import TransactionHistoryModal from "../components/TransactionHistoryModal";



const CashAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [movementsOpen, setMovementsOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  
  // Transfer dialog
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [transferData, setTransferData] = useState({
    from_type: "cash",
    from_account_id: "",
    to_type: "bank",
    to_account_id: "",
    amount: "",
    date: new Date().toISOString().split("T")[0],
    description: "",
  });
  
  // Transactions dialog
  const [transactionsDialogOpen, setTransactionsDialogOpen] = useState(false);
  const [transactionsAccount, setTransactionsAccount] = useState(null);
  
  const [formData, setFormData] = useState({
    name: "",
    currency: "TRY",
    initial_balance: "",
    notes: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [cashRes, bankRes] = await Promise.all([
        axios.get(`${API}/cash-accounts`),
        axios.get(`${API}/bank-accounts`),
      ]);
      setAccounts(cashRes.data);
      setBankAccounts(bankRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Veriler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error("Kasa adı zorunludur");
      return;
    }

    try {
      // Ensure initial_balance is a number, not an empty string
      const dataToSend = {
        ...formData,
        initial_balance: formData.initial_balance === "" || formData.initial_balance === null || formData.initial_balance === undefined 
          ? 0 
          : parseFloat(formData.initial_balance)
      };

      if (editingAccount) {
        await axios.put(`${API}/cash-accounts/${editingAccount.id}`, dataToSend);
        toast.success("Kasa güncellendi");
      } else {
        await axios.post(`${API}/cash-accounts`, dataToSend);
        toast.success("Kasa eklendi");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error("Failed to save account:", error);
      toast.error("İşlem başarısız");
    }
  };

  const handleEdit = (account) => {
    setEditingAccount(account);
    setFormData({
      name: account.name,
      currency: account.currency,
      initial_balance: account.initial_balance,
      notes: account.notes || "",
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu kasayı silmek istediğinizden emin misiniz?")) return;

    try {
      await axios.delete(`${API}/cash-accounts/${id}`);
      toast.success("Kasa silindi");
      fetchData();
    } catch (error) {
      console.error("Failed to delete account:", error);
      toast.error("Silme işlemi başarısız");
    }
  };

  const resetForm = () => {
    setEditingAccount(null);
    setFormData({
      name: "",
      currency: "TRY",
      initial_balance: "",
      notes: "",
    });
  };

  // Transfer functions
  const openTransferDialog = (account) => {
    setTransferData({
      from_type: "cash",
      from_account_id: account.id,
      to_type: "bank",
      to_account_id: "",
      amount: "",
      date: new Date().toISOString().split("T")[0],
      description: "",
    });
    setTransferDialogOpen(true);
  };

  const handleTransferSubmit = async (e) => {
    e.preventDefault();
    if (!transferData.to_account_id || !transferData.amount) {
      toast.error("Hedef hesap ve tutar zorunludur");
      return;
    }

    try {
      await axios.post(`${API}/account-transfers`, {
        ...transferData,
        amount: parseFloat(transferData.amount),
      });
      toast.success("Transfer başarılı");
      setTransferDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error("Failed to transfer:", error);
      toast.error(error.response?.data?.detail || "Transfer başarısız");
    }
  };

  // Transactions functions
  const openTransactionsDialog = (account) => {
    setTransactionsAccount(account);
    setTransactionsDialogOpen(true);
  };

  const totalBalance = accounts.reduce((sum, acc) => sum + acc.balance, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 print:space-y-4" data-testid="cash-accounts-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Kasa Hesapları
          </h1>
          <p className="text-slate-500 mt-1">Nakit kasalarınızı yönetin</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => window.print()} className="no-print">
            <Printer className="h-4 w-4 mr-2" /> Yazdır
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-slate-900 hover:bg-slate-800" data-testid="add-cash-account-btn">
                <Plus className="h-5 w-5 mr-2" />
                Yeni Kasa
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>{editingAccount ? "Kasa Düzenle" : "Yeni Kasa"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Kasa Adı *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ana Kasa"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Para Birimi</Label>
                    <Select value={formData.currency} onValueChange={(val) => setFormData({ ...formData, currency: val })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="TRY">TRY</SelectItem>
                        <SelectItem value="USD">USD</SelectItem>
                        <SelectItem value="EUR">EUR</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Açılış Bakiyesi</Label>
                    <CurrencyInput
                      value={formData.initial_balance}
                      onChange={(val) => setFormData({ ...formData, initial_balance: val })}
                      placeholder="0,00"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Not</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Ek notlar..."
                    rows={2}
                  />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>İptal</Button>
                  <Button type="submit" className="bg-slate-900 hover:bg-slate-800">
                    {editingAccount ? "Güncelle" : "Kaydet"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Summary */}
      <Card className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-emerald-100">Toplam Kasa Bakiyesi</p>
              <p className="text-4xl font-bold tabular-nums mt-1">
                ₺{totalBalance.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div className="h-16 w-16 rounded-full bg-white/20 flex items-center justify-center">
              <Wallet className="h-8 w-8" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Accounts Grid */}
      {accounts.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {accounts.map((account) => (
            <Card key={account.id} className="card-hover">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-emerald-50 flex items-center justify-center">
                      <Wallet className="h-5 w-5 text-emerald-600" />
                    </div>
                    <CardTitle className="text-lg">{account.name}</CardTitle>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => { setSelectedAccount(account); setMovementsOpen(true); }} className="text-blue-600 hover:text-blue-700 hover:bg-blue-50" title="Hesap Hareketleri">
                      <FileText className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(account)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(account.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Bakiye</span>
                  <span className={`text-2xl font-bold tabular-nums ${account.balance >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                    ₺{account.balance.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="text-xs text-slate-400">
                  Para Birimi: {account.currency}
                </div>
                <div className="flex gap-2 mt-2">
                  <Button onClick={() => openTransferDialog(account)} variant="outline" className="flex-1" data-testid={`transfer-cash-${account.id}`}>
                    <ArrowRightLeft className="h-4 w-4 mr-2" />
                    Transfer
                  </Button>
                  <Button onClick={() => openTransactionsDialog(account)} variant="outline" className="flex-1" data-testid={`transactions-cash-${account.id}`}>
                    <FileText className="h-4 w-4 mr-2" />
                    İşlemler
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <Wallet className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Henüz kasa eklenmemiş</p>
          </CardContent>
        </Card>
      )}

      <AccountMovements
        open={movementsOpen}
        onClose={() => setMovementsOpen(false)}
        accountType="cash"
        accountId={selectedAccount?.id}
        accountName={selectedAccount?.name}
      />

      {/* Transfer Dialog */}
      <Dialog open={transferDialogOpen} onOpenChange={setTransferDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="h-5 w-5" />
              Para Transferi
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleTransferSubmit} className="space-y-4">
            <div className="p-3 bg-emerald-50 rounded-lg">
              <p className="text-sm text-emerald-700">
                <strong>Kaynak:</strong> {accounts.find(a => a.id === transferData.from_account_id)?.name} (Kasa)
              </p>
            </div>
            <div className="space-y-2">
              <Label>Hedef Hesap Türü</Label>
              <Select value={transferData.to_type} onValueChange={(val) => setTransferData({ ...transferData, to_type: val, to_account_id: "" })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank">Banka</SelectItem>
                  <SelectItem value="cash">Kasa</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Hedef Hesap *</Label>
              <Select value={transferData.to_account_id} onValueChange={(val) => setTransferData({ ...transferData, to_account_id: val })}>
                <SelectTrigger><SelectValue placeholder="Hesap seçin" /></SelectTrigger>
                <SelectContent>
                  {(transferData.to_type === "bank" ? bankAccounts : accounts.filter(a => a.id !== transferData.from_account_id)).map((acc) => (
                    <SelectItem key={acc.id} value={acc.id}>{acc.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tutar *</Label>
                <CurrencyInput
                  value={transferData.amount}
                  onChange={(val) => setTransferData({ ...transferData, amount: val })}
                  placeholder="0,00"
                />
              </div>
              <div className="space-y-2">
                <Label>Tarih</Label>
                <Input type="date" value={transferData.date} onChange={(e) => setTransferData({ ...transferData, date: e.target.value })} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Açıklama</Label>
              <Textarea value={transferData.description} onChange={(e) => setTransferData({ ...transferData, description: e.target.value })} placeholder="Transfer açıklaması" rows={2} />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setTransferDialogOpen(false)}>İptal</Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">Transfer Yap</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Transactions Dialog */}
      <TransactionHistoryModal
        open={transactionsDialogOpen}
        onClose={() => setTransactionsDialogOpen(false)}
        accountType="cash"
        account={transactionsAccount}
        onDataRefresh={fetchData}
      />
    </div>
  );
};

export default CashAccounts;
